/**
 * Constants file for partner dashboard strings and enums
 */

export const SETTINGS_STRINGS = {
  ORGANIZATION_PROFILE: {
    TITLE: 'Partner Settings',
    DESCRIPTION: 'Manage your partner information and configuration',
    PARTNER_NAME_LABEL: 'Partner Name',
    PARTNER_NAME_PLACEHOLDER: 'Enter partner name',
    DOMAIN_LABEL: 'Domain',
    DOMAIN_PLACEHOLDER: 'partner.example.com',
    LOGO_URL_LABEL: 'Logo',
    LOGO_URL_PLACEHOLDER: 'https://example.com/logo.png',
    THEME_CONFIG_LABEL: 'Theme Configuration',
    FEATURE_CUSTOMIZATIONS_LABEL: 'Feature Customizations',
    SAVE_BUTTON: 'Save Changes',
    CANCEL_BUTTON: 'Cancel',
    SUCCESS_MESSAGE: 'Partner profile updated successfully',
    ERROR_MESSAGE: 'Failed to update partner profile',
  },
  API_CONFIGURATION: {
    TITLE: 'API Configuration',
    DESCRIPTION: 'Manage your API credentials',
    API_KEY_LABEL: 'API Key',
    API_SECRET_LABEL: 'API Secret',
    SHOW_BUTTON: 'Show',
    HIDE_BUTTON: 'Hide',
    COPY_BUTTON: 'Copy',
    COPIED_BUTTON: 'Copied!',
    REGENERATE_KEY_BUTTON: 'Regenerate API Key',
    REGENERATE_SECRET_BUTTON: 'Regenerate API Secret',
    REGENERATE_WARNING_TITLE: 'Regenerate API Credentials',
    REGENERATE_WARNING_MESSAGE:
      'This will invalidate the current credentials. All applications using the old credentials will need to be updated. This action cannot be undone.',
    REGENERATE_CONFIRM: 'Yes, Regenerate',
    SUCCESS_MESSAGE: 'API configuration updated successfully',
    ERROR_MESSAGE: 'Failed to update API configuration',
    REGENERATE_SUCCESS: 'API credentials regenerated successfully',
    COPY_SUCCESS: 'Copied to clipboard',
  },
  THEME_CONFIGURATION: {
    TITLE: 'Theme Configuration',
    DESCRIPTION: 'Customize the look and feel of your SDK',
    DARK_MODE_LABEL: 'Enable Dark Mode',
    COLOR_STYLES_LABEL: 'Color Styles',
    BORDER_RADIUS_LABEL: 'Border Radius',
    LIVE_PREVIEW_TITLE: 'Live Preview',
    SUCCESS_MESSAGE: 'Theme configuration updated successfully',
    ERROR_MESSAGE: 'Failed to update theme configuration',
  },
  NAVIGATION: {
    PROFILE: 'Profile',
    API: 'API',
    THEME: 'Theme',
    SETTINGS_TITLE: 'Settings',
  },
} as const;

export const API_ENDPOINTS = {
  PARTNER: {
    GET_PROFILE: '/partner/me',
    UPDATE_PROFILE: '/partner/profile',
    GET_SIGNING_KEYS: '/partner/signing-keys',
    CREATE_SIGNING_KEY: '/partner/signing-keys',
    DEACTIVATE_SIGNING_KEY: (keyId: string) => `/partner/signing-keys/${keyId}`,
    GET_STRIPO_TOKEN: '/partner/stripo/token',
    SEGMENT_DEFINITIONS: '/partner/segment-definitions',
    SEGMENT_TEXT_TO_SEGMENT: '/partner/segments/text-to-segment',
    SCHEMA_DEFINITIONS: '/partner/schema-definitions',
    VOICE_CALL_AGGREGATES: '/partner/voice/calls/aggregates',
    VOICE_CALL_AGGREGATES_SUMMARY: '/partner/voice/calls/aggregates/summary',
    AI_AUTOMATION_GENERATE_CONTENT: '/partner/ai-automation/generate-email-content',
  },
  AUTOMATION_TEMPLATES: {
    LIST: '/partner/automation-templates',
    GET: (id: string) => `/partner/automation-templates/${id}`,
    CREATE: '/partner/automation-templates',
    UPDATE: (id: string) => `/partner/automation-templates/${id}`,
    DUPLICATE_DEFAULTS: '/partner/automation-templates/duplicate-defaults',
    DEFAULT_METADATA: '/partner/automation-templates/default-metadata',
    COPY_DEFAULT_AUTOMATION: '/partner/automation-templates/copy-default-automation',
    CREATE_FROM_DEFAULT: '/partner/automation-templates/create-from-default',
    COPY: (id: string) => `/partner/automation-templates/${id}/copy`,
    SYNC: '/partner/automation-templates/sync',
    SYNC_STATUS: (templateId: string) => `/partner/automation-templates/sync/status/${templateId}`,
    VALIDATE_TENANT_SEGMENTS: (id: string) =>
      `/partner/automation-templates/${id}/validate-tenant-segments`,
    AUTOMATION_STATUSES: (id: string) => `/partner/automation-templates/${id}/automation-statuses`,
  },
  AUTOMATIONS: {
    PREVIEW_SEGMENT_RECIPIENTS: '/api/automations/internal/preview-segment-recipients',
  },
} as const;

export enum SettingsTab {
  PROFILE = 'profile',
  DATA_INTEGRATION = 'data-integration',
  THEME = 'theme',
  API = 'api',
}

/**
 * Client identification header value for Partner Dashboard
 * Used to identify requests coming from the partner dashboard in traces
 */
export const REACH_CLIENT_HEADER_VALUE = 'Partner Dashboard';
